import { Box, Button, Link, Paper, Stack, Typography, Chip, Tooltip, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RestaurantIcon from '@mui/icons-material/Restaurant';

/**
 * Barre latérale de la home : affiche jusqu'à deux blocs Paper distincts,
 * empilés verticalement dans la même cellule de la grille de Home.jsx.
 *
 *   1. (optionnel) Bloc client — visible si une préréservation a déjà été
 *      faite depuis ce navigateur (localStorage). Bandeau bleu « Bonjour X »,
 *      bouton d'accès direct, statut réel de la résa (chips réservation /
 *      en attente / annulée). Bouton discret « Ce n'est pas moi ».
 *
 *   2. Bloc capacité globale — toujours visible. Chip total, barre empilée
 *      (confirmé + en attente), chip places restantes / liste d'attente.
 */
export default function CapacityBar({ capacity, loading, error, remembered, clientStatus, onForget }) {
  // Tints exposés par le thème (cf. theme.js) → adaptatifs dark/light.
  const theme = useTheme();
  const t = theme.palette.tints ?? {};

  // Statut côté client (déduit du lookup) : confirmé / en attente / annulé.
  const myState = clientStatus?.reservation_state; // 'reservation' | 'preregistration' | 'cancelled'
  const myMeals = clientStatus?.meals ?? 0;
  const myConfirmedMeals = myState === 'reservation' ? myMeals : 0;
  const myPendingMeals   = myState === 'preregistration' ? myMeals : 0;
  const myCancelled      = myState === 'cancelled';

  const total = capacity?.capacity ?? 0;
  const confirmed = capacity?.confirmed_meals ?? 0;
  const pending = capacity?.pending_meals ?? 0;
  const reserved = capacity?.reserved ?? confirmed + pending;
  const remaining = capacity?.remaining ?? Math.max(0, total - reserved);
  const isFull = capacity?.is_full;

  // Pourcentages cappés à 100 (la barre ne dépasse jamais visuellement).
  const pctConfirmed = total > 0 ? Math.min(100, (confirmed / total) * 100) : 0;
  const pctTotal     = total > 0 ? Math.min(100, (reserved  / total) * 100) : 0;
  const pctReadable  = Math.round(pctTotal);

  // Fonds clairs pour rendre les deux boîtes plus vivantes.
  // Les couleurs viennent du thème (tints) → s'adaptent au mode dark/light.
  // - Bloc client : teinte bleue (rappelle le bandeau primaire à l'intérieur).
  // - Bloc global : teinte chaude (évoque les frites / l'ambiance soirée).
  const clientPaperSx = {
    p: { xs: 1.5, md: 2 },
    border: `1px solid ${t.clientBorder ?? 'transparent'}`,
    backgroundColor: t.clientBg ?? 'transparent',
  };
  const globalPaperSx = {
    p: { xs: 1.5, md: 2 },
    border: `1px solid ${t.globalBorder ?? 'transparent'}`,
    backgroundColor: t.globalBg ?? 'transparent',
  };

  return (
    <Stack spacing={1.5} sx={{ height: '100%' }}>
      {/* ===== Bloc 1 : info CLIENT ==================================== */}
      {remembered && (
        <Paper elevation={0} sx={clientPaperSx}>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <MarkEmailReadOutlinedIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {remembered.first_name
                  ? `Bonjour ${remembered.first_name}`
                  : 'Préréservation enregistrée'}
              </Typography>
            </Stack>
            {/*
              Deux variantes selon ce qu'on a en mémoire :
                - UUID connu (mémorisation issue du formulaire) → accès direct au récap
                - UUID inconnu (mémorisation issue de /retrouver) → on renvoie sur
                  /retrouver pour relancer le flow magic-link.
            */}
            <Button
              component={RouterLink}
              to={remembered.id ? `/payer/${remembered.id}` : '/retrouver'}
              size="small"
              variant="contained"
              color="inherit"
              endIcon={<OpenInNewOutlinedIcon />}
              sx={{
                mt: 0.75,
                color: 'primary.main',
                // Fond mode-aware : blanc casse en light, surface sombre en
                // dark. Evite le rendu d'un bouton blanc dur dans un bloc
                // primary.light teinte fonce en dark.
                backgroundColor: 'background.paper',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              fullWidth
            >
              {remembered.id ? 'Voir mon récap' : 'Recevoir mon lien'}
            </Button>
            {onForget && (
              <Box sx={{ mt: 0.5, textAlign: 'center' }}>
                <Link
                  component="button"
                  type="button"
                  onClick={onForget}
                  underline="hover"
                  variant="caption"
                  sx={{ color: 'inherit', opacity: 0.85 }}
                >
                  Ce n'est pas moi
                </Link>
              </Box>
            )}
          </Box>

          {/* Chips de statut spécifiques au client (lookup côté API). */}
          {clientStatus && (
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              justifyContent="center"
              useFlexGap
              sx={{ mt: 1 }}
            >
              <Tooltip title="Repas dont le paiement est enregistré">
                <Chip
                  icon={<EventAvailableIcon />}
                  size="small"
                  label={`${myConfirmedMeals} réservation${myConfirmedMeals > 1 ? 's' : ''}`}
                  color={myConfirmedMeals > 0 ? 'primary' : 'default'}
                  variant={myConfirmedMeals > 0 ? 'filled' : 'outlined'}
                />
              </Tooltip>
              {myPendingMeals > 0 && (
                <Tooltip title="Repas préréservés, en attente de confirmation par le club">
                  <Chip
                    icon={<HourglassEmptyIcon />}
                    size="small"
                    label={`${myPendingMeals} en attente`}
                    variant="outlined"
                  />
                </Tooltip>
              )}
              {myCancelled && (
                <Chip
                  icon={<CancelOutlinedIcon />}
                  size="small"
                  label="Annulée"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </Paper>
      )}

      {/* ===== Bloc 2 : capacité GLOBALE ============================== */}
      <Paper elevation={0} sx={{ ...globalPaperSx, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack
          spacing={1.25}
          sx={{ flex: 1, justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}
        >
          <Chip
            icon={<RestaurantIcon />}
            label={`${reserved} / ${total} repas`}
            color={isFull ? 'warning' : 'default'}
            sx={{ fontWeight: 600 }}
          />

          <Box sx={{ width: '100%' }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">Remplissage</Typography>
              <Typography variant="body2" fontWeight={700}>{pctReadable} %</Typography>
            </Stack>
            {/*
              Barre empilée maison :
                - fond gris (capacité libre)
                - segment "pending" (semi-transparent primary)
                - segment "confirmed" superposé (primary plein)
            */}
            {/* Fond de barre piloté par le thème : assez contrasté en clair, et
                blanc translucide en sombre pour rester lisible. */}
            <Box sx={{ position: 'relative', height: 12, borderRadius: 6, backgroundColor: t.barTrack ?? 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: `${pctTotal}%`,
                  backgroundColor: isFull ? 'warning.light' : 'primary.light',
                  opacity: 0.55,
                  transition: 'width 320ms ease',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: `${pctConfirmed}%`,
                  backgroundColor: isFull ? 'warning.main' : 'primary.main',
                  transition: 'width 320ms ease',
                }}
              />
            </Box>
          </Box>

          {!isFull && remaining > 0 && (
            <Chip
              size="small"
              label={`${remaining} place${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''}`}
              color="success"
              variant="outlined"
            />
          )}
          {isFull && (
            <Chip size="small" label="Complet — plus de réservation possible" color="warning" />
          )}
        </Stack>

        {loading && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
            Chargement…
          </Typography>
        )}
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, textAlign: 'center' }}>
            {error}
          </Typography>
        )}
      </Paper>
    </Stack>
  );
}
